import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthCard } from '@/core/auth/components/AuthCard';

import { AuthButton } from '@/core/auth/components/AuthButton';

import { AuthInput } from '@/core/auth/components/AuthInput';

import { STORAGE_KEYS } from '@/constants/storage';

import { useAppDispatch } from '@/hooks/useAppDispatch';

import { useAppSelector } from '@/hooks/useAppSelector';

import { clearAuth, completeSsoLogin, verifyMfaLogin } from '@/store/authSlice';

import { getOrCreateDeviceId } from '@/core/auth/deviceId';

import {

  clearPlatformOnboardingRequired,

  isFattahOneProductSelectionRequired,

  isPlatformOnboardingRequired,

  requireFattahOneOnboarding,

  writeOnboardingState,

  type OnboardingModule,

} from '@/modules/onboarding/onboardingState';

import { getModuleEntryByErpModule } from '@/config/moduleEntries';

import { getPostLoginPath } from '@/utils/moduleScope';

import { isOrgOwner, resolveOwnerHomePath } from '@/utils/moduleAccess';

import { resolvePostLoginDestination } from '@/utils/postLoginNavigation';

import { MODULE_HUB_PATH, ORG_SIGN_IN_PATH } from '@/constants/authRoutes';

import { notify } from '@/core/toast/notify';
import { i18n } from '@/i18n';
import { showLoginSuccessToast } from '@/utils/authTransition';

import type { LoginResponse } from '@/types/auth';

import type { AppDispatch } from '@/store';

import {

  resolveSsoExchangeError,

  notifySsoError,

  ssoErrorToastGuardKey,

  ssoExchangeGuardKey,

  ssoExchangeMessageKey,

} from '@/utils/ssoErrorMessage';



const ONBOARDING_MODULES = new Set<OnboardingModule>([

  'POS',

  'CRM',

  'HR',

  'FINANCE',

  'PROJECTS',

  'DESK',

  'SOCIAL',

]);



function resolveOnboardingModule(code?: string): OnboardingModule | undefined {

  if (!code) return undefined;

  const normalized = code.trim().toUpperCase() as OnboardingModule;

  return ONBOARDING_MODULES.has(normalized) ? normalized : undefined;

}



type SsoContext = {

  redirect?: string | null;

  newAccount: boolean;

  pendingEntryModule?: string;

};



function isMasterConsoleRedirect(redirect?: string | null) {

  return redirect === '/master' || (redirect?.startsWith('/master/') ?? false);

}



function navigateAfterLogin(

  navigate: ReturnType<typeof useNavigate>,

  dispatch: AppDispatch,

  result: LoginResponse,

  context: SsoContext,

) {

  const { redirect, newAccount, pendingEntryModule } = context;

  if (isMasterConsoleRedirect(redirect)) {

    if (!result.masterAdmin) {

      dispatch(clearAuth());

      notify.error(i18n.t('errors.thisAccountIsNotAPlatformMasterAdministrator', { ns: 'auth' }));

      navigate('/master/login', { replace: true });

      return;

    }

    navigate('/master', { replace: true });

    return;

  }

  if (result.masterAdmin) {

    dispatch(clearAuth());

    notify.error(i18n.t('errors.platformOperatorsMustSignInFromTheMaster', { ns: 'auth' }));

    navigate('/master/login', { replace: true });

    return;

  }

  if (result.forcePasswordChange) {

    navigate('/change-password', { replace: true });

    return;

  }

  if (newAccount) {

    const intentModule = resolveOnboardingModule(pendingEntryModule);

    requireFattahOneOnboarding();

    writeOnboardingState({

      step: 0,

      intentModule,

      selectedModules: intentModule ? [intentModule] : ['POS'],

      selectedPlan: 'FREE',

    });

  }

  if (pendingEntryModule) {

    clearPlatformOnboardingRequired();

  }

  if (newAccount || isFattahOneProductSelectionRequired()) {

    navigate(redirect?.startsWith('/') ? redirect : '/onboarding/products', { replace: true });

    return;

  }

  if (isPlatformOnboardingRequired()) {

    navigate('/onboarding', { replace: true });

    return;

  }

  if (result.entryModule || pendingEntryModule) {

    const moduleCode = (result.entryModule ?? pendingEntryModule) as string;

    const home =

      redirect?.startsWith('/')

        ? redirect

        : getModuleEntryByErpModule(moduleCode)?.homePath ?? MODULE_HUB_PATH;

    navigate(getPostLoginPath(moduleCode as never, home), { replace: true });

    return;

  }

  if (isOrgOwner(result.permissions ?? [], result.roleCode)) {

    const home = redirect?.startsWith('/') ? redirect : resolveOwnerHomePath(result.enabledModules ?? []);

    navigate(home, { replace: true });

    return;

  }

  navigate(resolvePostLoginDestination(result, redirect), { replace: true });

}



export function OAuthCallbackPage() {
  const { t } = useTranslation('auth');

  const [params] = useSearchParams();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const loading = useAppSelector((s) => s.auth.loading);

  const [error, setError] = useState<string | null>(null);

  const [mfaToken, setMfaToken] = useState<string | null>(null);

  const [totpCode, setTotpCode] = useState('');

  const [ssoContext, setSsoContext] = useState<SsoContext | null>(null);

  const [exchanging, setExchanging] = useState(true);



  useEffect(() => {

    const exchangeToken = params.get('exchangeToken');

    const redirect = params.get('redirect');

    const newAccount = params.get('newAccount') === 'true';

    const entryModuleParam = params.get('entryModule') ?? undefined;

    const pendingEntryModule =

      entryModuleParam ?? sessionStorage.getItem(STORAGE_KEYS.PENDING_ENTRY_MODULE) ?? undefined;



    if (!exchangeToken) {

      setError('Google sign-in did not complete. Please try again.');

      setExchanging(false);

      return;

    }



    const guardKey = ssoExchangeGuardKey(exchangeToken);

    const messageKey = ssoExchangeMessageKey(exchangeToken);

    const guardState = sessionStorage.getItem(guardKey);



    if (guardState === 'done') {
      setExchanging(false);
      if (redirect?.startsWith('/')) {
        navigate(redirect, { replace: true });
      } else if (newAccount) {
        navigate('/onboarding/products', { replace: true });
      } else {
        navigate('/dashboard/home', { replace: true });
      }
      return;
    }

    if (guardState === 'failed') {

      setError(sessionStorage.getItem(messageKey) ?? 'Google sign-in could not be completed.');

      setExchanging(false);

      return;

    }

    if (guardState !== 'pending') {
      sessionStorage.setItem(guardKey, 'pending');
    }

    const pictureUrl = params.get('pictureUrl');

    const context: SsoContext = { redirect, newAccount, pendingEntryModule };

    setSsoContext(context);



    void (async () => {

      try {

        const result = await dispatch(

          completeSsoLogin({

            exchangeToken,

            deviceId: getOrCreateDeviceId(),

            entryModule: pendingEntryModule,

            oauthPictureUrl: pictureUrl ?? undefined,

          }),

        ).unwrap();

        if (sessionStorage.getItem(`${guardKey}:completed`) === '1') {
          return;
        }
        sessionStorage.setItem(`${guardKey}:completed`, '1');
        sessionStorage.setItem(guardKey, 'done');
        sessionStorage.removeItem(messageKey);

        if (result.mfaRequired && result.mfaToken) {

          setMfaToken(result.mfaToken);

          setExchanging(false);

          notify.info('Enter the code from your authenticator app', { icon: '🔐' });

          return;

        }

        navigateAfterLogin(navigate, dispatch, result, context);

        showLoginSuccessToast(
          newAccount ? 'Account created with Google' : 'Signed in with Google',
          `sso:${exchangeToken}`,
        );

      } catch (err) {

        const message = resolveSsoExchangeError(err, newAccount ? 'register' : 'login');

        sessionStorage.setItem(guardKey, 'failed');

        sessionStorage.setItem(messageKey, message);

        notifySsoError(message, ssoErrorToastGuardKey('oauth-callback', exchangeToken));

        setError(message);

        setExchanging(false);

      }

    })();

  }, [dispatch, navigate, params]);



  const onMfaSubmit = async (e: React.FormEvent) => {

    e.preventDefault();

    if (!mfaToken || !ssoContext || totpCode.length < 6) return;

    try {

      const result = await dispatch(

        verifyMfaLogin({

          mfaToken,

          totpCode,

          entryModule: ssoContext.pendingEntryModule,

          masterLogin: isMasterConsoleRedirect(ssoContext.redirect),

        }),

      ).unwrap();

      navigateAfterLogin(navigate, dispatch, result, ssoContext);
      showLoginSuccessToast('Signed in successfully', `sso-mfa:${mfaToken}`);

    } catch (err) {

      notifySsoError(

        resolveSsoExchangeError(err, 'login'),

        ssoErrorToastGuardKey('oauth-mfa', mfaToken),

      );

    }

  };



  if (mfaToken) {

    return (

      <AuthCard title={t('pages.twoFactorAuthentication')}>

        <form onSubmit={onMfaSubmit} className="space-y-4">

          <AuthInput

            label={t('labels.authenticatorCode')}

            inputMode="numeric"

            autoComplete="one-time-code"

            value={totpCode}

            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}

          />

          <AuthButton type="submit" loading={loading} disabled={totpCode.length < 6}>

            Verify and continue

          </AuthButton>

        </form>

      </AuthCard>

    );

  }



  return (

    <AuthCard title={t('pages.completingLogin')}>

      {error ? (

        <div className="space-y-3 text-center">

          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>

          <Link to={ORG_SIGN_IN_PATH} className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400">

            Back to login

          </Link>

        </div>

      ) : exchanging ? (

        <div className="flex justify-center py-8">

          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />

        </div>

      ) : null}

    </AuthCard>

  );

}


